if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          const button = mediaToSwitch.querySelector('button');
          if (!button) return;

          button.addEventListener('click', () => {
            const mediaId = mediaToSwitch.dataset.target;
            this.setActiveMedia(mediaId, false);
            this.selectVariantForMedia(mediaId);
          });
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      getMediaVariants() {
        if (this.mediaVariants) return this.mediaVariants;

        const dataElement = this.querySelector('[data-vf-media-variants]');
        if (!dataElement) {
          this.mediaVariants = [];
          return this.mediaVariants;
        }

        try {
          const parsedData = JSON.parse(dataElement.textContent);
          this.mediaVariants = Array.isArray(parsedData) ? parsedData : [];
        } catch (error) {
          console.error('Unable to read media variant data:', error);
          this.mediaVariants = [];
        }

        return this.mediaVariants;
      }

      selectVariantForMedia(mediaId) {
        const numericMediaId = Number(String(mediaId).split('-').pop());
        if (!numericMediaId) return;

        const variant = this.getMediaVariants().find(
          ({ featured_media_id: featuredMediaId }) => Number(featuredMediaId) === numericMediaId
        );
        if (!variant) return;

        const productInfo = this.closest('product-info');
        const variantSelectors = productInfo?.querySelector('variant-selects');
        if (!variantSelectors) return;

        const currentVariantId = Number(
          productInfo.querySelector(
            `#product-form-${productInfo.dataset.section} input[name="id"], input[name="id"]`
          )?.value
        );
        if (currentVariantId === Number(variant.id)) return;

        const optionGroups = Array.from(
          variantSelectors.querySelectorAll('fieldset, .product-form__input--dropdown')
        );
        let eventTarget = null;

        variant.options.forEach((optionValue, optionIndex) => {
          const group = optionGroups[optionIndex];
          if (!group) return;

          const select = group.matches('select') ? group : group.querySelector('select');
          if (select) {
            const matchingOption = Array.from(select.options).find((option) => option.value === optionValue);
            if (!matchingOption) return;

            Array.from(select.options).forEach((option) => option.removeAttribute('selected'));
            select.value = optionValue;
            matchingOption.setAttribute('selected', 'selected');
            eventTarget = select;
            return;
          }

          const radios = Array.from(group.querySelectorAll('input[type="radio"]'));
          const matchingRadio = radios.find((radio) => radio.value === optionValue);
          if (!matchingRadio) return;

          radios.forEach((radio) => {
            radio.checked = radio === matchingRadio;
          });
          eventTarget = matchingRadio;
        });

        if (!eventTarget) return;
        eventTarget.dispatchEvent(new Event('change', { bubbles: true }));
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}


/* ============================================================
   VF / Pebble & Wick — Cinematic Media Gallery Enhancements
   Safe layer on top of Dawn media-gallery.js.
   Keeps gallery switching, thumbnails, live region, media playback and sticky header behavior intact.
   ============================================================ */

(() => {
  const MediaGalleryElement = customElements.get('media-gallery');
  if (!MediaGalleryElement || MediaGalleryElement.prototype.__vfCinematicPatched) return;

  MediaGalleryElement.prototype.__vfCinematicPatched = true;

  const originalSetActiveMedia = MediaGalleryElement.prototype.setActiveMedia;
  const originalSetActiveThumbnail = MediaGalleryElement.prototype.setActiveThumbnail;
  const originalAnnounceLiveRegion = MediaGalleryElement.prototype.announceLiveRegion;
  const originalPlayActiveMedia = MediaGalleryElement.prototype.playActiveMedia;
  const originalRemoveListSemantic = MediaGalleryElement.prototype.removeListSemantic;

  const root = document.documentElement;

  const setGalleryState = (gallery, state, detail = {}) => {
    gallery.dataset.vfGalleryState = state;

    gallery.classList.toggle('vf-media-gallery-changing', state === 'changing');
    gallery.classList.toggle('vf-media-gallery-ready', state === 'ready');
    gallery.classList.toggle('vf-media-gallery-playing', state === 'playing');
    gallery.classList.toggle('vf-media-gallery-announcing', state === 'announcing');

    root.classList.toggle('vf-media-gallery-active', state === 'changing' || state === 'playing');

    gallery.dispatchEvent(
      new CustomEvent(`vf:media-gallery-${state}`, {
        bubbles: true,
        detail
      })
    );
  };

  const markGallery = (gallery) => {
    if (!gallery || gallery.__vfCinematicEnhanced) return;
    gallery.__vfCinematicEnhanced = true;

    gallery.classList.add('vf-media-gallery-enhanced');

    const viewer = gallery.elements?.viewer || gallery.querySelector('[id^="GalleryViewer"]');
    const thumbnails = gallery.elements?.thumbnails || gallery.querySelector('[id^="GalleryThumbnails"]');

    viewer?.classList.add('vf-media-gallery__viewer');
    thumbnails?.classList.add('vf-media-gallery__thumbnails');

    gallery.querySelectorAll('[data-media-id]').forEach((media, index) => {
      media.classList.add('vf-media-gallery__item');
      media.style.setProperty('--vf-media-index', index);
    });

    gallery.querySelectorAll('[data-target]').forEach((thumbnail, index) => {
      thumbnail.classList.add('vf-media-gallery__thumbnail');
      thumbnail.style.setProperty('--vf-thumbnail-index', index);

      const button = thumbnail.querySelector('button');
      if (button) {
        button.classList.add('vf-media-gallery__thumbnail-button');
        button.setAttribute('aria-label', button.getAttribute('aria-label') || `View media ${index + 1}`);
      }
    });

    setGalleryState(gallery, 'ready', {
      initial: true,
      mediaCount: gallery.querySelectorAll('[data-media-id]').length
    });
  };

  const pulseMedia = (element, className = 'vf-media-gallery-pulse', duration = 760) => {
    if (!element) return;

    element.classList.remove(className);
    void element.offsetWidth;
    element.classList.add(className);

    window.setTimeout(() => {
      element.classList.remove(className);
    }, duration);
  };

  MediaGalleryElement.prototype.setActiveMedia = function vfSetActiveMedia(mediaId, prepend) {
    const activeBefore = this.elements?.viewer?.querySelector('.is-active[data-media-id]')?.dataset.mediaId;

    setGalleryState(this, 'changing', {
      mediaId,
      prepend,
      previousMediaId: activeBefore
    });

    const result = originalSetActiveMedia.call(this, mediaId, prepend);

    const activeMedia =
      this.elements?.viewer?.querySelector(`[data-media-id="${mediaId}"]`) ||
      this.elements?.viewer?.querySelector('.is-active[data-media-id]');

    if (activeMedia) {
      this.elements?.viewer
        ?.querySelectorAll('[data-media-id]')
        .forEach((media) => media.classList.toggle('vf-media-gallery__item-active', media === activeMedia));

      pulseMedia(activeMedia, 'vf-media-gallery__item-pulse', 820);
    }

    window.requestAnimationFrame(() => {
      setGalleryState(this, 'ready', {
        mediaId,
        prepend,
        activeMedia
      });

      this.dispatchEvent(
        new CustomEvent('vf:media-gallery-media-changed', {
          bubbles: true,
          detail: {
            mediaId,
            previousMediaId: activeBefore,
            activeMedia,
            prepend
          }
        })
      );
    });

    return result;
  };

  MediaGalleryElement.prototype.setActiveThumbnail = function vfSetActiveThumbnail(thumbnail) {
    const result = originalSetActiveThumbnail.call(this, thumbnail);

    if (thumbnail) {
      this.elements?.thumbnails
        ?.querySelectorAll('[data-target]')
        .forEach((item) => item.classList.toggle('vf-media-gallery__thumbnail-active', item === thumbnail));

      pulseMedia(thumbnail, 'vf-media-gallery__thumbnail-pulse', 560);

      this.dispatchEvent(
        new CustomEvent('vf:media-gallery-thumbnail-changed', {
          bubbles: true,
          detail: {
            thumbnail,
            mediaId: thumbnail.dataset.target,
            mediaPosition: thumbnail.dataset.mediaPosition
          }
        })
      );
    }

    return result;
  };

  MediaGalleryElement.prototype.announceLiveRegion = function vfAnnounceLiveRegion(activeItem, position) {
    setGalleryState(this, 'announcing', {
      activeItem,
      position
    });

    const result = originalAnnounceLiveRegion.call(this, activeItem, position);

    window.setTimeout(() => {
      this.classList.remove('vf-media-gallery-announcing');
      if (this.dataset.vfGalleryState === 'announcing') {
        setGalleryState(this, 'ready', {
          activeItem,
          position
        });
      }
    }, 2100);

    return result;
  };

  MediaGalleryElement.prototype.playActiveMedia = function vfPlayActiveMedia(activeItem) {
    const hasDeferredMedia = Boolean(activeItem?.querySelector?.('.deferred-media'));

    if (hasDeferredMedia) {
      setGalleryState(this, 'playing', {
        activeItem
      });
    }

    const result = originalPlayActiveMedia.call(this, activeItem);

    if (hasDeferredMedia) {
      window.setTimeout(() => {
        setGalleryState(this, 'ready', {
          activeItem
        });
      }, 900);
    }

    return result;
  };

  MediaGalleryElement.prototype.removeListSemantic = function vfRemoveListSemantic() {
    const result = originalRemoveListSemantic.call(this);

    this.classList.add('vf-media-gallery-presentation-mode');

    return result;
  };

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('media-gallery').forEach(markGallery);
  }, { once: true });

  document.addEventListener('click', (event) => {
    const thumbnailButton = event.target.closest?.('media-gallery [data-target] button');
    if (!thumbnailButton) return;

    const thumbnail = thumbnailButton.closest('[data-target]');
    const gallery = thumbnail.closest('media-gallery');

    gallery?.classList.add('vf-media-gallery-thumbnail-clicked');
    thumbnail?.classList.add('vf-media-gallery__thumbnail-clicked');

    window.setTimeout(() => {
      gallery?.classList.remove('vf-media-gallery-thumbnail-clicked');
      thumbnail?.classList.remove('vf-media-gallery__thumbnail-clicked');
    }, 520);
  });

  const galleryObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;

        if (node.matches?.('media-gallery')) {
          markGallery(node);
        }

        node.querySelectorAll?.('media-gallery').forEach(markGallery);

        const gallery = node.closest?.('media-gallery');
        if (gallery) {
          gallery.__vfCinematicEnhanced = false;
          markGallery(gallery);
        }
      });
    });
  });

  if (document.body) {
    galleryObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }
})();
